import {
  postOpsAnalyticsAPI,
  postOpsAnalyticsBlobAPI,
} from "../../../../api/Networking";
import { APIBlobResponse } from "../../../../models/NetworkResponse";
import {
  OpsAnalyticsFilters,
  OpsTatRecordScope,
  OpsTatSort,
  resolveOpsInwardDevPageSize,
} from "../../../../models/Dashboard/OpsTat/OpsTatFilters";
import {
  OpsDashboardMetricId,
  OpsTatDrilldownRequest,
  OpsTatDrilldownResult,
} from "../../../../models/Dashboard/OpsTat/OpsTatDrilldown";
import { OpsTatSummaryResult } from "../../../../models/Dashboard/OpsTat/OpsTatSummary";
import {
  OpsInwardDevSummaryResult,
  OpsInwardDevVendorsResult,
} from "../../../../models/Dashboard/OpsTat/OpsInwardDeviation";

export default class NewOperationsDashboardAPI {
  getSummary = async (
    filters: OpsAnalyticsFilters,
  ): Promise<OpsTatSummaryResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        "/v1/ops/tat/summary",
        filters,
      );
      if (!response.success || !response.result) {
        throw new Error(response.message || "Failed to fetch ops TAT summary.");
      }
      return response.result as OpsTatSummaryResult;
    } catch (error) {
      throw error;
    }
  };

  getInwardDevSummary = async (
    filters: OpsAnalyticsFilters,
  ): Promise<OpsInwardDevSummaryResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        "/v1/ops/inward-dev/summary",
        filters,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops inward deviation summary.",
        );
      }
      return response.result as OpsInwardDevSummaryResult;
    } catch (error) {
      throw error;
    }
  };

  getInwardDevVendors = async (
    filters: OpsAnalyticsFilters,
  ): Promise<OpsInwardDevVendorsResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        "/v1/ops/inward-dev/vendors",
        filters,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops inward deviation vendors.",
        );
      }
      return response.result as OpsInwardDevVendorsResult;
    } catch (error) {
      throw error;
    }
  };

  getDrilldown = async (
    metricId: OpsDashboardMetricId,
    body: OpsTatDrilldownRequest,
  ): Promise<OpsTatDrilldownResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        `/v1/ops/tat/drilldown/${metricId}`,
        body,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops TAT drilldown.",
        );
      }
      return response.result as OpsTatDrilldownResult;
    } catch (error) {
      throw error;
    }
  };

  getInwardDevDrilldown = async (
    metricId: OpsDashboardMetricId,
    body: OpsTatDrilldownRequest,
  ): Promise<OpsTatDrilldownResult> => {
    try {
      const response = await postOpsAnalyticsAPI(
        `/v1/ops/inward-dev/drilldown/${metricId}`,
        {
          ...body,
          page_size: resolveOpsInwardDevPageSize(body.page_size, metricId),
        },
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch ops inward deviation drilldown.",
        );
      }
      return response.result as OpsTatDrilldownResult;
    } catch (error) {
      throw error;
    }
  };

  exportDrilldownCsv = async (
    metricId: OpsDashboardMetricId,
    filters: OpsAnalyticsFilters,
    extras?: {
      record_scope?: OpsTatRecordScope;
      sort?: OpsTatSort;
    },
  ): Promise<APIBlobResponse> => {
    try {
      return await this.exportCsvBlob(
        `/v1/ops/tat/drilldown/${metricId}`,
        filters,
        extras,
      );
    } catch (error) {
      throw error;
    }
  };

  exportInwardDevCsv = async (
    metricId: OpsDashboardMetricId,
    filters: OpsAnalyticsFilters,
    extras?: {
      record_scope?: OpsTatRecordScope;
      sort?: OpsTatSort;
    },
  ): Promise<APIBlobResponse> => {
    try {
      return await this.exportCsvBlob(
        `/v1/ops/inward-dev/drilldown/${metricId}`,
        filters,
        extras,
      );
    } catch (error) {
      throw error;
    }
  };

  private exportCsvBlob = async (
    path: string,
    filters: OpsAnalyticsFilters,
    extras?: {
      record_scope?: OpsTatRecordScope;
      sort?: OpsTatSort;
    },
  ): Promise<APIBlobResponse> => {
    const body: OpsTatDrilldownRequest = {
      ...filters,
      format: "csv",
    };
    if (extras?.record_scope) {
      body.record_scope = extras.record_scope;
    }
    if (extras?.sort) {
      body.sort = extras.sort;
    }

    return await postOpsAnalyticsBlobAPI(path, body);
  };
}
